import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getPasses } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Shield, Sun, Moon, LogOut, User, Car, Search,
  ArrowLeft, Archive as ArchiveIcon, CalendarIcon, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

export default function ArchivePage() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      const res = await getPasses(true); // archived = true
      setPasses(res.data);
    } catch (error) {
      toast.error('Ошибка загрузки архива');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBack = () => {
    if (user?.role === 'admin') {
      navigate('/admin');
    } else if (user?.role === 'security') {
      navigate('/security');
    } else {
      navigate('/dashboard');
    }
  };

  const filteredPasses = passes.filter(pass => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    if (pass.pass_type === 'person') {
      return pass.person_data?.full_name?.toLowerCase().includes(searchLower) ||
             pass.person_data?.passport_number?.includes(search);
    } else {
      return pass.vehicle_data?.license_plate?.toLowerCase().includes(searchLower);
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={handleBack} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-muted">
                  <ArchiveIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h1 className="text-lg font-black tracking-tighter">АРХИВ</h1>
                  <p className="text-xs text-muted-foreground">Завершённые заявки</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
                {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </Button>
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">{user?.full_name}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по имени или номеру..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 max-w-md"
              data-testid="archive-search"
            />
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Архивные заявки</span>
              <Badge variant="secondary">{filteredPasses.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[calc(100vh-300px)]">
              {filteredPasses.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  {search ? 'Ничего не найдено' : 'Архив пуст'}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredPasses.map((pass, index) => (
                    <Card 
                      key={pass.id} 
                      className="animate-fade-in bg-muted/30"
                      style={{ animationDelay: `${index * 0.03}s` }}
                      data-testid={`archive-pass-${pass.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`p-2 rounded-md ${pass.pass_type === 'person' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                              {pass.pass_type === 'person' ? (
                                <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                              ) : (
                                <Car className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                              )}
                            </div>
                            <div className="space-y-1">
                              <div className="font-semibold">
                                {pass.pass_type === 'person' 
                                  ? pass.person_data?.full_name 
                                  : pass.vehicle_data?.license_plate}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {pass.pass_type === 'person' 
                                  ? pass.person_data?.visit_purpose
                                  : `${pass.vehicle_data?.car_brand} ${pass.vehicle_data?.car_model}`}
                              </div>
                              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <CalendarIcon className="h-3 w-3" />
                                  {format(new Date(pass.visit_date), 'dd.MM.yyyy')}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {pass.visit_time}
                                </span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Заявитель: {pass.created_by_name}
                              </div>
                              {pass.notes && (
                                <div className="text-xs text-muted-foreground italic mt-1">
                                  Примечание: {pass.notes}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right text-xs text-muted-foreground">
                            {pass.arrived_at && (
                              <div>Прибыл: {format(new Date(pass.arrived_at), 'dd.MM HH:mm')}</div>
                            )}
                            {pass.departed_at && (
                              <div>Выехал: {format(new Date(pass.departed_at), 'dd.MM HH:mm')}</div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
